import { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns, generateColumnsFromDynProps } from '../../utils/helper';

import { DataGridIdentifiers, OrderArrowsUI, DatagridPremiumUi } from '../Interface';
import projectService from '../../service/project.service';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useGenerateJokerReportCols from '../../hooks/useGenerateJokerReportCols';

export default function ProjectReports({ projectState }) {
  const {
    setRefetchTable,
    setSelectedValues,
    viewField,
    operatorsDisabled,
    refetchTable,
    rowSelectionModel,
    setRowSelectionModel,
    dynProps,
    autoCompleteOptions,
    companyContactsState,
    params,
    jokerFields,
  } = projectState;
  const { companyContacts } = companyContactsState;
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'projectName', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const orderProject = (projectId, newOrder) => {
    projectService.orderProject(projectId, { newOrder }).then(() => {
      setRefetchTable(true);
    });
  };

  const handleOrderChange = (projectId, direction) => {
    orderProject(projectId, direction);
  };

  const getList = (gridState) => {
    projectService.getProjects(gridState).then((data) => {
      setReports(data);
    });
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'projectName',
        headerName: 'Projekt neve',
        type: 'string',
      },
      {
        field: 'companyId',
        type: 'singleSelect',
        headerName: 'Cég',
        renderCell: (data) => {
          return data.row.company?.name;
        },
        valueOptions: autoCompleteOptions.company,
      },
      ...(params.PROJECT_SHOW_PROJECT_TYPE_STATUS !== 'Y'
        ? [
            {
              field: 'statusId',
              type: 'singleSelect',
              headerName: 'Státusz',
              renderCell: (data) => {
                return data.row.status?.itemName;
              },
              valueOptions: autoCompleteOptions.status,
            },
          ]
        : []),
      {
        field: 'projectStartDate',
        type: 'date',
        headerName: 'Kezdő időpont',
        valueGetter: (_data, row) => {
          return new Date(row.projectStartDate);
        },
      },
      {
        field: 'projectEndDate',
        type: 'date',
        headerName: 'Befejező időpont',
        valueGetter: (_data, row) => {
          return row.projectEndDate ? new Date(row.projectEndDate) : null;
        },
      },
      {
        field: 'projectPrepare',
        headerName: '%',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          const preparedness = data.row.projectPrepare;
          return preparedness > 0 ? `${preparedness}%` : '';
        },
      },
      {
        field: 'visOrder',
        headerName: 'Sorrend',
        type: 'action',
        renderCell: (params) => {
          const disabled = params?.api?.getSortModel()[0]?.field !== 'visOrder';
          const isSortedDescending = params.api.getSortModel()[0]?.sort === 'desc';

          const handleUpClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.projectId, 'down');
            } else {
              handleOrderChange(params.row.projectId, 'up');
            }
          };

          const handleDownClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.projectId, 'up');
            } else {
              handleOrderChange(params.row.projectId, 'down');
            }
          };

          return <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />;
        },
      },
      {
        field: 'orderNumber',
        headerName: getProjectOrderNumberName(params),
        type: 'string',
      },
      ...(params.HIDE_PROJECT_COMPANYCONTACT_FIELD !== 'Y'
        ? [
            {
              field: 'companyContactId',
              type: 'string',
              headerName: 'Kapcsolattartó',
              filterable: false,
              renderCell: (data) => {
                return data.row.companyContact?.name;
              },
            },
          ]
        : []),
      {
        field: 'active',
        headerName: 'Aktív',
        type: 'boolean',
      },
      {
        field: 'projectTypeId',
        type: 'singleSelect',
        headerName: 'Projekt típus',
        renderCell: (data) => {
          return data.row.projectType?.name;
        },
        valueOptions: autoCompleteOptions.projectType,
      },
      {
        field: 'classificationId',
        type: 'singleSelect',
        headerName: 'Besorolás',
        renderCell: (data) => {
          return data.row.classification?.name;
        },
        valueOptions: autoCompleteOptions.classification,
      },
      ...(params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y'
        ? [
            {
              field: 'projectTypeStatusId',
              type: 'singleSelect',
              headerName: 'Típus státusz',
              valueOptions: autoCompleteOptions.projectTypeStatus,
              renderCell: (data) => {
                return data.row.projectTypeStatus?.name;
              },
            },
          ]
        : []),
      ...(params.PROJECT_COSTCENTER_FIELD === 'Y'
        ? [
            {
              field: 'costCenterCode',
              type: 'singleSelect',
              headerName: 'Költséghely',
              renderCell: (data) => {
                const findedCostCenter = autoCompleteOptions.costCenter?.find(
                  (item) => item.value === data.row.costCenterCode
                )?.label;
                return findedCostCenter;
              },
              valueOptions: autoCompleteOptions.costCenter,
            },
          ]
        : []),
      ...(params.PROJECT_COMPANY_LOCATION_FIELD === 'Y'
        ? [
            {
              field: 'companyLocationId',
              type: 'singleSelect',
              headerName: 'Telephely',
              renderCell: (data) => {
                const findedCompanyLocation = autoCompleteOptions.companyLocation?.find(
                  (item) => item.value === data.row.companyLocationId
                )?.label;
                return findedCompanyLocation;
              },
              valueOptions: autoCompleteOptions.companyLocation,
            },
          ]
        : []),
      {
        field: 'userId',
        type: 'singleSelect',
        headerName: 'Rögzítette',
        renderCell: (data) => {
          return data.row.user?.nickName;
        },
        valueOptions: autoCompleteOptions.user,
      },
      ...generateColumnsFromDynProps(dynProps ?? []),
      ...jokerCols,
    ]);
  }, [autoCompleteOptions, dynProps, params, jokerCols]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.Projects}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      getRowId={(row) => row.projectId}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
      gridExport={true}
    />
  );
}
